import { FirstPartyDeviceType } from "../../shared.js";
import { hourMs, minuteMs, secondMs } from "../../timeConstants.js";
import { isValidNum, potentialMongoInstanceToPlainObject } from "../../utils/index.js";
import { AreaOfInterest } from "../areaOfInterest/areaOfInterest.js";
import type { Device } from "../device/device.js";
import type { SensorSpecifier } from "../device/subSchemas/sensorSpecifier.js";
import { Organization } from "../organization/organization.js";
import { SensorReadingConfiguration } from "./sensorReadingConfiguration.js";
import { CustomSensorTimingRule } from "./subSchemas/customSensorTimingRule.js";
import { SensorTypeReadingConfig } from "./subSchemas/sensorTypeReadingConfig.js";
import { SensorParametersForCustomTimingRules } from "./types.js";

/**
 * Default timing values for sensor readings and uplinks.
 * These are legacy defaults and should not be modified without careful consideration.
 */
const defaultReading = hourMs;
const defaultUplink = 80 * minuteMs;
/**
 * Basic sensor configuration defining default timing for all sensor types.
 * This serves as the baseline configuration when no user or AOI-specific settings are available.
 * Note: Schema key must be spelled out to avoid circular dependencies.
 */
const basicSensorConfig: SensorReadingConfiguration = {
    schemaKey: "SensorReadingConfiguration",
    configurationBySensorType: {
        irrometer: new SensorTypeReadingConfig({
            readingPeriod: defaultReading,
            uplinkPeriod: defaultUplink,
        }),
        teros_vol: new SensorTypeReadingConfig({
            readingPeriod: defaultReading,
            uplinkPeriod: defaultUplink,
        }),
        v1flowmeter: new SensorTypeReadingConfig({
            readingPeriod: defaultReading,
            uplinkPeriod: defaultUplink,
        }),
        v1temphum: new SensorTypeReadingConfig({
            readingPeriod: defaultReading,
            uplinkPeriod: defaultUplink,
        }),
        v1transducer: new SensorTypeReadingConfig({
            readingPeriod: defaultReading,
            uplinkPeriod: defaultUplink,
        }),
        watermark: new SensorTypeReadingConfig({
            readingPeriod: defaultReading,
            uplinkPeriod: defaultUplink,
        }),
    },
    customRules: [],
    readingPeriod: 30 * minuteMs,
    uplinkPeriod: 80 * minuteMs,
};

/**
 * Parameters required for calculating sensor reading frequency.
 */
type ParamsForGettingSensorReadingFrequency = {
    sensor: SensorSpecifier;
    device: Device;
    aoi?: AreaOfInterest;
    owner?: Organization;
};

/**
 * Represents a sensor frequency configuration with priority and source tracking.
 * Used to determine which configuration takes precedence in the cascading ruleset.
 */
type PrioritizedSensorFrequency = {
    uplinkPeriod: number;
    readingPeriod: number;
    integerReadingsPerUplink?: number;
    priority: number;
    source: string;
};

/**
 * Priority levels for cascading sensor frequency configuration.
 * Higher values take precedence over lower values.
 */
enum Priorities {
    DEVICE_SPECIFIC = 100,
    AOI_RULE = 90,
    USER_RULE = 80,
    AOI_SETTINGS = 70,
    USER_SETTINGS = 50,
    SYSTEM_DEFAULT = 0,
}

/**
 * Extracts sensor parameters from the device needed for evaluating custom timing rules.
 */
function getParametersForSensor({
    sensor,
    device,
}: ParamsForGettingSensorReadingFrequency): SensorParametersForCustomTimingRules {
    const sensorType = sensor.sensorType;
    const deviceType: FirstPartyDeviceType = device.DeviceType as FirstPartyDeviceType;
    let sensorCount = 0;
    if (device?.connectedSensors && Array.isArray(device?.connectedSensors)) {
        device?.connectedSensors.forEach((cs) => {
            if (cs && cs.sensorType) {
                sensorCount += 1;
            }
        });
    }
    let notes = "";
    if (device?.notes) {
        notes = device?.notes;
    }

    return {
        sensorType,
        deviceType,
        sensorCount,
        notes,
    };
}

/**
 * Builds a prioritized list of all applicable sensor frequency configurations.
 * Converts Mongo documents to plain objects and collects configurations from:
 * - System defaults
 * - Hardcoded sensor defaults
 * - User-level settings and custom rules
 * - AOI-level settings and custom rules
 */
export function getPrioritizedSensorFrequencyList({
    sensor,
    device,
    aoi,
    owner,
}: ParamsForGettingSensorReadingFrequency): PrioritizedSensorFrequency[] {
    sensor = potentialMongoInstanceToPlainObject(sensor);
    device = potentialMongoInstanceToPlainObject(device);
    owner = potentialMongoInstanceToPlainObject(owner);
    aoi = potentialMongoInstanceToPlainObject(aoi);

    const results: PrioritizedSensorFrequency[] = [
        {
            source: "systemDefault",
            priority: 0,
            uplinkPeriod: 20 * minuteMs,
            readingPeriod: 5 * minuteMs,
        },
    ];

    const sensorParams = getParametersForSensor({ sensor, device, aoi, owner });

    const configs = [
        {
            source: "hardcodedSensorDefaults",
            priority: 1,
            ...basicSensorConfig,
        },
    ];
    const customRules: (CustomSensorTimingRule & { priority: number; source: string })[] = [];

    // Collect user-level configurations and custom rules
    if (owner && owner.sensorReadingConfiguration) {
        configs.push({
            schemaKey: "SensorReadingConfiguration",
            source: "ownerSensorConfig",
            priority: Priorities.USER_SETTINGS,
            ...owner.sensorReadingConfiguration,
        });
        if (owner.sensorReadingConfiguration.customRules.length > 0) {
            customRules.push(
                ...owner.sensorReadingConfiguration.customRules.map((rule, i) => ({
                    source: `ownerRules.${i}`,
                    priority: Priorities.USER_RULE + 1 / (i + 1),
                    ...rule,
                })),
            );
        }
    }

    // Collect AOI-level configurations and custom rules
    // AOI rules have higher priority than user-wide rules
    if (aoi && aoi.sensorReadingConfiguration) {
        configs.push({
            schemaKey: "SensorReadingConfiguration",
            source: "aoiSensorConfig",
            priority: Priorities.AOI_SETTINGS,
            ...aoi.sensorReadingConfiguration,
        });
        customRules.push(
            ...aoi.sensorReadingConfiguration.customRules.map((rule, i) => ({
                source: `aoiRules.${i}`,
                priority: Priorities.AOI_RULE + 1 / (i + 1),
                ...rule,
            })),
        );
    }

    // Process each configuration and extract sensor-specific and default settings
    configs.forEach((config) => {
        const newSensorSpecificResult = {
            source: config.source + `.${sensorParams.sensorType}`,
            priority: config.priority + 0.5,
            uplinkPeriod: config?.configurationBySensorType?.[sensorParams.sensorType]?.uplinkPeriod,
            readingPeriod: config?.configurationBySensorType?.[sensorParams.sensorType]?.readingPeriod,
        };

        if (newSensorSpecificResult.uplinkPeriod && newSensorSpecificResult.readingPeriod) {
            results.push(newSensorSpecificResult);
        }

        const newSensorNonSpecificResult = {
            source: config.source + `.default`,
            priority: config.priority,
            uplinkPeriod: config?.uplinkPeriod,
            readingPeriod: config?.readingPeriod,
        };

        if (newSensorNonSpecificResult.uplinkPeriod && newSensorNonSpecificResult.readingPeriod) {
            results.push(newSensorNonSpecificResult);
        }
    });

    // Evaluate custom rules and add matching configurations
    customRules.forEach((rule) => {
        if (CustomSensorTimingRule.utilities.doesSensorSatisfyRule(rule, sensorParams)) {
            results.push({
                source: rule.source,
                priority: rule.priority,
                readingPeriod: rule.readingPeriod,
                uplinkPeriod: rule.uplinkPeriod,
            });
        }
    });

    // Sort by priority (highest first)
    results.sort((a, b) => b.priority - a.priority);
    return results;
}

/**
 * Calculates the ideal sensing frequency for a sensor based on a cascading ruleset.
 *
 * Priority order (highest to lowest):
 * - 100: Device-specific rules or settings (not yet implemented)
 * - 90: AOI-specific rules that the device qualifies for
 * - 80: User-specific rules that the device qualifies for
 * - 70: AOI-specific sensor type settings
 * - 60: AOI-specific default settings
 * - 50: User-specific sensor type settings
 * - 40: User-specific default settings
 * - 30: Basic sensor config sensor type settings
 * - 20: Fallback defaults (60 min reading, 80 min uplink)
 *
 * The function validates configurations based on device generation and returns warnings
 * for invalid configurations.
 *
 * For generation 3 devices (Sprout), additional constraints apply:
 * - Periods must be multiples of 1 minute
 * - Maximum period is 254 minutes
 * - Readings per uplink must be between 1-30
 *
 * @param sensor - The sensor to configure
 * @param device - The device the sensor is connected to
 * @param aoi - The area of interest the sensor is in
 * @param owner - The owner/user of the sensor
 * @returns Configuration with periods, source, and any validation warnings
 */
export function calculateReadingFrequencyForSensor({
    sensor,
    device,
    aoi,
    owner,
}: ParamsForGettingSensorReadingFrequency) {
    sensor = potentialMongoInstanceToPlainObject(sensor);
    device = potentialMongoInstanceToPlainObject(device);
    owner = potentialMongoInstanceToPlainObject(owner);
    aoi = potentialMongoInstanceToPlainObject(aoi);

    const warnings = [];

    const results = getPrioritizedSensorFrequencyList({ sensor, device, aoi, owner });

    // Find the first valid result by iterating through prioritized configurations
    const finalResult: PrioritizedSensorFrequency = results.reduce<undefined | PrioritizedSensorFrequency>(
        (champ, unCastResult) => {
            if (champ) {
                return champ;
            }
            const result: PrioritizedSensorFrequency = {
                uplinkPeriod: unCastResult.uplinkPeriod,
                readingPeriod: unCastResult.readingPeriod,
                integerReadingsPerUplink: Math.round(unCastResult.uplinkPeriod / (unCastResult.readingPeriod || 1)),
                priority: unCastResult.priority,
                source: unCastResult.source,
            };

            // Validate that periods are valid numbers
            if (!isValidNum(result.uplinkPeriod) || !isValidNum(result.readingPeriod)) {
                warnings.push(
                    `device ${device.node_id} cannot have reading/uplink set to ${result.readingPeriod}/${result.uplinkPeriod} ` +
                        `because one or more are NaN`,
                );
                return undefined;
            }

            // Validate minimum period lengths
            if (result.uplinkPeriod < minuteMs || result.readingPeriod < 30 * secondMs) {
                warnings.push(
                    `device ${device.node_id} cannot have reading/uplink set to ${result.readingPeriod}/${result.uplinkPeriod} ` +
                        `because it is too short a time period`,
                );
                return undefined;
            }

            return result;
        },
        undefined,
    );
    return {
        uplinkPeriod: finalResult.uplinkPeriod,
        readingPeriod: finalResult.readingPeriod,
        integerReadingsPerUplink: finalResult.integerReadingsPerUplink,
        source: finalResult.source,
        warnings: warnings,
    };
}
